import React from "react";
import Tooltip from "@mui/material/Tooltip";
import TimelineIcon from "@mui/icons-material/Timeline";
import SchoolIcon from '@mui/icons-material/School';
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import ViewHeadlineIcon from "@mui/icons-material/ViewHeadline";
import QuizOutlinedIcon from "@mui/icons-material/QuizOutlined";
import StyleOutlinedIcon from "@mui/icons-material/StyleOutlined";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import TopBarDisplayCreateTrainingContent from "../home/top_bar_components/TopBarDisplayCreateTrainingContent";
import TracksDisplay from "./TracksDisplay";
import ModulesDisplay from "./ModulesDisplay";
import SectionsDisplay from "./SectionsDisplay";
import QuizzesDisplay from "./QuizzesDisplay";
import SwipeDecksDisplay from "./SwipeDecksDisplay";
import SwipeDeckInputsModal from "./SwipeDeckInputsModal";
import FormsDisplay from "./FormsDisplay";
import FormEditorModal from "./FormEditorModal";
import RoleplaysDisplay from "./RoleplaysDisplay";
import RoleplayEditorModal from "./RoleplayEditorModal";
import BadgesDisplay from "./BadgesDisplay";
import BadgeEditorModal from "./BadgeEditorModal";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import ConnectWithoutContactIcon from "@mui/icons-material/ConnectWithoutContact";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import TrackGenerationStudio from "./TrackGenerationStudio";
import { denormalizeEditorDeckForPreview } from "./swipeDeckHelpers";
import {
  API_get_learning_tracks,
  API_get_learning_modules,
  API_get_learning_section_summaries,
  API_get_learning_quiz_summaries,
  API_post_learning_track,
  API_delete_learning_track,
  API_post_learning_module,
  API_delete_learning_module,
  API_post_learning_section,
  API_delete_learning_section,
  API_post_learning_quiz,
  API_delete_learning_quiz,
  API_get_role_names2,
  API_post_learning_track_enrollment_by_roles,
} from "../../../constants";
import LoadSpinner from "../../../utilities/LoadSpinner";
import { getRightworkAPI } from "../requests/utilities/requests";
import { postRightworkAPI } from "../requests/utilities/requests";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// Lazy-load the swipe deck player so its bundle (~66KB gzipped) is only
// fetched when an admin actually opens a preview. Defined after imports
// to satisfy the `import/first` lint rule — `React.lazy(() => import(...))`
// is module body code, not an import statement.
const SwipeDeckPlayerLazy = React.lazy(() =>
  import(
    "../take_training_employee/swipe_deck_player/SwipeDeckPlayer"
  ),
);

function CreateTrainingContentDisplay(props) {
  const [display, setDisplay] = React.useState("Tracks");
  // When the AI Studio is entered via a "Generate with AI" dropdown on the
  // New Track / New Module button, seed which target the studio's kind picker
  // starts on. Null = opened from the AI Studio tab (studio uses its default).
  const [studioInitialKind, setStudioInitialKind] = React.useState(null);
  const handleGenerateWithAi = (kind) => {
    setStudioInitialKind(kind);
    setDisplay("AiStudio");
  };

  const homeCompany = props.storeNames?.filter(
    (s) => s.id === props.homeStore
  )[0]["company"];

  const default_data_track = {
    create_at: null,
    modules: [],
    name: "",
    description: "",
    update_at: null,
    uuid: null,
    roles_assigned_to: [],
    completion_due_date: null,
    completion_due_days_after_enrollment: null,
    grants_badge: null,
    status: "draft",
  };
  const [modalDataTrack, setModalDataTrack] =
    React.useState(default_data_track);

  const default_data_module = {
    color: "bg-slate-400",
    created_at: null,
    description: "",
    name: "",
    sections: [],
    updated_at: null,
    uuid: null,
    category: ""
  };
  const [modalDataModule, setModalDataModule] =
    React.useState(default_data_module);

  const default_data_section = {
    created_at: null,
    name: "",
    updated_at: null,
    uuid: null,
    as_json: [],
    category: ""
  };
  const [modalDataSection, setModalDataSection] =
    React.useState(default_data_section);

  const default_data_quiz = {
    created_at: null,
    learning_questions: [
      // {
      //   answers: [],
      //   created_at: null,
      //   question: "",
      //   updated_at: null,
      //   uuid: null
      // }
    ],
    name: "",
    updated_at: null,
    uuid: null,
    // null (not 0) so QuizEditorModal's init effect leaves the
    // "Require minimum to pass" toggle UNchecked for new quizzes.
    // With 0, the toggle would render checked + the input shows "0",
    // and a quick save would ship a quiz where 0% is the pass
    // threshold (every learner trivially passes).
    pass_percentage: null,
    retake_limit: null,
    show_correct_answers: true,
    category: ""
  };
  const [modalDataQuiz, setModalDataQuiz] = React.useState(default_data_quiz);

  // Swipe Decks modal state. Editor renders with `modalDataDeck = { uuid }`
  // for edits, or null for create. Refresh of the dashboard list happens
  // via `swipeDeckRefreshKey` increment — remounting SwipeDecksDisplay
  // forces it to re-fetch.
  const [swipeDeckModalOpen, setSwipeDeckModalOpen] = React.useState(false);
  const [modalDataDeck, setModalDataDeck] = React.useState(null);
  const [swipeDeckRefreshKey, setSwipeDeckRefreshKey] = React.useState(0);

  // Forms (reusable form asset) management state — mirrors swipe decks.
  const [formModalOpen, setFormModalOpen] = React.useState(false);
  const [modalDataForm, setModalDataForm] = React.useState(null);
  const [formsRefreshKey, setFormsRefreshKey] = React.useState(0);

  // Practice scenario (AI conversation practice asset) management state — mirrors forms.
  const [roleplayModalOpen, setRoleplayModalOpen] = React.useState(false);
  const [modalDataRoleplay, setModalDataRoleplay] = React.useState(null);
  const [roleplaysRefreshKey, setRoleplaysRefreshKey] = React.useState(0);

  // Training badges management state — mirrors practice scenarios.
  const [badgeModalOpen, setBadgeModalOpen] = React.useState(false);
  const [modalDataBadge, setModalDataBadge] = React.useState(null);
  const [badgesRefreshKey, setBadgesRefreshKey] = React.useState(0);

  // Player-preview state. Two flavors:
  //   { previewDeck }  — admin previewing in-progress (unsaved) editor state
  //   { deckUuid }     — admin previewing a saved deck from the dashboard
  // The player accepts either via its previewMode + previewDeck props.
  const [swipeDeckPreviewState, setSwipeDeckPreviewState] = React.useState(null);

  const [data, setData] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(false);

  const default_api_calls = {
    tracks: API_get_learning_tracks,
    modules: API_get_learning_modules,
    sections: API_get_learning_section_summaries,
    quizzes: API_get_learning_quiz_summaries,
    roleNames: API_get_role_names2,
  };

  const api_params = {
    company_id: homeCompany,
    store_id: props.homeStore,
  };

  const fetch_data = async (api_calls, api_params, showLoading) => {
    setIsLoading(showLoading);
    let api_set_data_name = Object.keys(api_calls);
    let api_list = Object.values(api_calls);
    try {
      // console.log("CALLING APIS");
      const res = await Promise.all(
        api_list.map((api) => getRightworkAPI(api, api_params))
      );
      var tempData = { ...data };
      res.map((res, i) => (tempData[api_set_data_name[i]] = res.data));
      setData(tempData);
      setIsLoading(false);
    } catch (error) {
      // console.log(error);
      //throw Error(error);
    }
  };

  React.useEffect(() => {
    fetch_data(default_api_calls, api_params, true);
  }, []);

  // ----------- POST APIs ----------- //

  // [POST] api/lms_info/post_learning_track (create/ update)
  // * learning_track_uuid (optional)
  // * company_id
  // * name
  // * learning_module_uuids (JSON stringified ordered list of UUIDs)

  const post_learning_track = async (trackDataOverride = null) => {
    // Use provided track data or fall back to state
    const trackData = trackDataOverride || modalDataTrack;
    const moduleUUIDs =
      trackData.modules?.map((module) => module.uuid) || [];
    const rolesAssignedToIDs =
      trackData.roles_assigned_to?.map((role) => role.id) || [];
    // console.log("ROLESID", rolesAssignedToIDs);

    // Prepare data to send
    // Keep completion_due_date as YYYY-MM-DD string - backend expects this format
    const requestData = {
      learning_track_uuid: trackData.uuid,
      company_id: homeCompany,
      name: trackData.name,
      description: trackData.description || "",
      learning_module_uuids: JSON.stringify(moduleUUIDs),
      roles_assigned_to: JSON.stringify(rolesAssignedToIDs),
      completion_due_date: trackData.completion_due_date, // Keep as YYYY-MM-DD string
      completion_due_days_after_enrollment:
        trackData.completion_due_days_after_enrollment ?? null,
      cover_file_uuid: trackData.cover?.uuid || "",
      grants_badge_uuid: trackData.grants_badge?.uuid || "",
    };

    try {
      toast.loading("Saving Track...", { autoClose: false });

      const res = await postRightworkAPI(API_post_learning_track, requestData);

      if (res.status === 200) {
        toast.dismiss();
        toast.success("Track Saved Successfully", { autoClose: 3000 });

        await fetch_data(default_api_calls, api_params, false);

        // Return the created/updated track with UUID from backend
        return res.data.redux?.payload?.learning_track;
      }
    } catch (error) {
      toast.dismiss();

      if (error.response?.data?.system?.message) {
        toast.error(error.response?.data?.system?.message, { autoClose: 3000 });
        // console.log("Error Info", error.response?.data?.system?.data);
      } else {
        toast.error("Request Failed", { autoClose: 3000 });
      }

      console.error("Error:", error); // Handle error as needed
    }
  };

  // [POST] api/lms_info/delete_learning_track
  // * company_id
  // * learning_track_uuid
  const delete_learning_track = async () => {
    // Prepare data to send
    const requestData = {
      company_id: homeCompany,
      learning_track_uuid: modalDataTrack.uuid,
    };

    try {
      toast.loading("Deleting Track...", { autoClose: false });

      const res = await postRightworkAPI(
        API_delete_learning_track,
        requestData
      );

      if (res.status === 200) {
        toast.dismiss();
        toast.success("Track Deleted Successfully", { autoClose: 3000 });

        fetch_data(default_api_calls, api_params, false);
      }
    } catch (error) {
      toast.dismiss();

      if (error.response?.data?.system?.message) {
        toast.error(error.response?.data?.system?.message, { autoClose: 3000 });
        // console.log("Error Info", error.response?.data?.system?.data);
      } else {
        toast.error("Request Failed", { autoClose: 3000 });
      }

      console.error("Error:", error); // Handle error as needed
    }
  };

  //   [POST] api/lms_info/post_learning_module (create/ update)
  // * learning_module_uuid (optional)
  // * company_id
  // * name
  // * color
  // * learning_section_uuids (JSON stringified ordered list of UUIDs)
  const post_learning_module = async (moduleDataOverride = null) => {
    // Use provided module data or fall back to state
    const moduleData = moduleDataOverride || modalDataModule;
    const sectionUUIDs =
      moduleData.sections?.map((section) => section.uuid) || [];

    // Prepare data to send
    const requestData = {
      learning_module_uuid: moduleData.uuid,
      company_id: homeCompany,
      name: moduleData.name,
      description: moduleData.description || "",
      color: moduleData.color,
      learning_section_uuids: JSON.stringify(sectionUUIDs),
      category: moduleData.category,
    };

    try {
      toast.loading("Saving Module...", { autoClose: false });

      const res = await postRightworkAPI(API_post_learning_module, requestData);

      if (res.status === 200) {
        toast.dismiss();
        toast.success("Module Saved Successfully", { autoClose: 3000 });

        await fetch_data(default_api_calls, api_params, false);

        // Return the created/updated module with UUID from backend
        return res.data.redux?.payload?.learning_module;
      }
    } catch (error) {
      toast.dismiss();

      if (error.response?.data?.system?.message) {
        toast.error(error.response?.data?.system?.message, { autoClose: 3000 });
        // console.log("Error Info", error.response?.data?.system?.data);
      } else {
        toast.error("Request Failed", { autoClose: 3000 });
      }

      console.error("Error:", error); // Handle error as needed
    }
  };

  // [POST] api/lms_info/delete_learning_module
  // * company_id
  // * learning_module_uuid
  const delete_learning_module = async () => {
    // Prepare data to send
    const requestData = {
      company_id: homeCompany,
      learning_module_uuid: modalDataModule.uuid,
    };

    try {
      toast.loading("Deleting Module...", { autoClose: false });

      const res = await postRightworkAPI(
        API_delete_learning_module,
        requestData
      );

      if (res.status === 200) {
        toast.dismiss();
        toast.success("Module Deleted Successfully", { autoClose: 3000 });

        fetch_data(default_api_calls, api_params, false);
      }
    } catch (error) {
      toast.dismiss();

      if (error.response?.data?.system?.message) {
        toast.error(error.response?.data?.system?.message, { autoClose: 3000 });
        // console.log("Error Info", error.response?.data?.system?.data);
      } else {
        toast.error("Request Failed", { autoClose: 3000 });
      }

      console.error("Error:", error); // Handle error as needed
    }
  };

  // [POST] api/lms_info/post_learning_section (create/ update)
  // * learning_section_uuid (optional)
  // * company_id
  // * name
  // * as_json
  const post_learning_section = async (duplicate) => {
    const sectionUUIDs =
      modalDataModule?.sections?.map((section) => section.uuid) || [];

    // NOTE: For any newly created files, we must swap out the file URL with our
    //       internal Rightwork file UUID.
    const editedJson = modalDataSection.as_json.map((element) => {
      const validTypes = ["image", "video", "audio", "file", "pdf"];
      if (!validTypes.includes(element.type)) {
        return element;
      }
      if ("file_uuid" in element.props) {
        return element;
      }

      let mapSignedUrlToFileUuid =
        localStorage.getItem("mapSignedUrlToFileUuid") || "{}";
      mapSignedUrlToFileUuid = JSON.parse(mapSignedUrlToFileUuid);

      const uuidVal = mapSignedUrlToFileUuid[element.props.url];
      if (uuidVal) {
        element.props.file_uuid = mapSignedUrlToFileUuid[element.props.url];
      }
      return element;
    });
    // console.log(editedJson);

    // Prepare data to send
    const requestData = {
      learning_section_uuid: duplicate ? null : modalDataSection.uuid,
      company_id: homeCompany,
      name: modalDataSection.name,
      as_json: editedJson,
      category: modalDataSection.category,
      // Only sent once known, so a save that never loaded the rule keeps it.
      ...(modalDataSection.test_out_quiz !== undefined && {
        test_out_quiz_uuid: modalDataSection.test_out_quiz?.uuid || "",
      }),
    };

    try {
      toast.loading(`${duplicate ? "Duplicating" : "Saving"} Section...`, {
        autoClose: false,
      });

      const res = await postRightworkAPI(
        API_post_learning_section,
        requestData
      );

      if (res.status === 200) {
        toast.dismiss();
        toast.success(
          `Section ${duplicate ? "Duplicated" : "Saved"} Successfully`,
          { autoClose: 3000 }
        );

        fetch_data(default_api_calls, api_params, false);

        // Return the created/updated section with UUID from backend
        return res.data.redux?.payload?.learning_section;
      }
    } catch (error) {
      toast.dismiss();

      if (error.response?.data?.system?.message) {
        toast.error(error.response?.data?.system?.message, { autoClose: 3000 });
        // console.log("Error Info", error.response?.data?.system?.data);
      } else {
        toast.error("Request Failed", { autoClose: 3000 });
      }

      console.error("Error:", error); // Handle error as needed
      // null tells the section editor to stay open, so a refused save keeps the edits.
      return null;
    }
  };

  // [POST] api/lms_info/delete_learning_section
  // * company_id
  // * learning_section_uuid
  const delete_learning_section = async () => {
    // Prepare data to send
    const requestData = {
      company_id: homeCompany,
      learning_section_uuid: modalDataSection.uuid,
    };

    try {
      toast.loading("Deleting Section...", { autoClose: false });

      const res = await postRightworkAPI(
        API_delete_learning_section,
        requestData
      );

      if (res.status === 200) {
        toast.dismiss();
        toast.success("Section Deleted Successfully", { autoClose: 3000 });

        fetch_data(default_api_calls, api_params, false);
      }
    } catch (error) {
      toast.dismiss();

      if (error.response?.data?.system?.message) {
        toast.error(error.response?.data?.system?.message, { autoClose: 3000 });
        // console.log("Error Info", error.response?.data?.system?.data);
      } else {
        toast.error("Request Failed", { autoClose: 3000 });
      }

      console.error("Error:", error); // Handle error as needed
    }
  };

  // [POST] api/lms_info/post_learning_quiz (create)
  // * company_id
  // * learning_quiz_uuid
  // * name
  // * learning_questions (JSON.stringify)
  //     * uuid (either valid UUID or null)
  //     * company_id
  //     * quiz_id
  //     * order_index
  //     * question
  //     * answers
  //     * answer_indices
  const post_learning_quiz = async (duplicate) => {
    // Prepare data to send
    const requestData = {
      company_id: homeCompany,
      learning_quiz_uuid: duplicate ? null : modalDataQuiz.uuid,
      name: duplicate ? `${modalDataQuiz.name}` : modalDataQuiz.name,
      pass_percentage: modalDataQuiz.pass_percentage,
      retake_limit: modalDataQuiz.retake_limit,
      show_correct_answers: modalDataQuiz.show_correct_answers,
      learning_questions: JSON.stringify(modalDataQuiz.learning_questions),
      is_duplicate: duplicate,
      category: modalDataQuiz.category,
      save_to_profile: modalDataQuiz.save_to_profile,
      // Only sent once known, so a save that never loaded it can't change it.
      ...(modalDataQuiz.gate_on_pass !== undefined && {
        gate_on_pass: modalDataQuiz.gate_on_pass !== false,
      }),
      ...(modalDataQuiz.shuffle_questions !== undefined && {
        shuffle_questions: Boolean(modalDataQuiz.shuffle_questions),
      }),
    };

    try {
      toast.loading(`${duplicate ? "Duplicating" : "Saving"} Quiz...`, {
        autoClose: false,
      });

      const res = await postRightworkAPI(API_post_learning_quiz, requestData);

      if (res.status === 200) {
        toast.dismiss();
        toast.success(
          `Quiz ${duplicate ? "Duplicated" : "Saved"} Successfully`,
          { autoClose: 3000 }
        );

        fetch_data(default_api_calls, api_params, false);
        return res.data.redux?.payload?.learning_quiz;
      }
    } catch (error) {
      toast.dismiss();

      if (error.response?.data?.system?.message) {
        toast.error(error.response?.data?.system?.message, { autoClose: 3000 });
        // console.log("Error Info", error.response?.data?.system?.data);
      } else {
        toast.error("Request Failed", { autoClose: 3000 });
      }

      console.error("Error:", error); // Handle error as needed
      return null;
    }
  };

  // [POST] api/lms_info/delete_learning_quiz
  // * company_id
  // * learning_quiz_uuid
  const delete_learning_quiz = async () => {
    // Prepare data to send
    const requestData = {
      company_id: homeCompany,
      learning_quiz_uuid: modalDataQuiz.uuid,
    };

    try {
      toast.loading("Deleting Quiz...", { autoClose: false });

      const res = await postRightworkAPI(API_delete_learning_quiz, requestData);

      if (res.status === 200) {
        toast.dismiss();
        toast.success("Quiz Deleted Successfully", { autoClose: 3000 });

        fetch_data(default_api_calls, api_params, false);
      }
    } catch (error) {
      toast.dismiss();

      if (error.response?.data?.system?.message) {
        toast.error(error.response?.data?.system?.message, { autoClose: 3000 });
        // console.log("Error Info", error.response?.data?.system?.data);
      } else {
        toast.error("Request Failed", { autoClose: 3000 });
      }

      console.error("Error:", error); // Handle error as needed
    }
  };

  // [POST] lms_info/post_learning_track_enrollment_by_roles
  // * company_id
  // * learning_track_uuid
  const post_learning_track_enrollment_by_roles = async () => {
    // Prepare data to send
    const requestData = {
      company_id: homeCompany,
      learning_track_uuid: modalDataTrack.uuid,
    };

    try {
      toast.loading("Publishing Track...", { autoClose: false });

      const res = await postRightworkAPI(
        API_post_learning_track_enrollment_by_roles,
        requestData
      );

      if (res.status === 200) {
        toast.dismiss();
        toast.success("Published Track Successfully", { autoClose: 3000 });

        fetch_data(default_api_calls, api_params, false);
      }
    } catch (error) {
      toast.dismiss();

      if (error.response?.data?.system?.message) {
        toast.error(error.response?.data?.system?.message, { autoClose: 3000 });
        // console.log("Error Info", error.response?.data?.system?.data);
      } else {
        toast.error("Request Failed", { autoClose: 3000 });
      }

      console.error("Error:", error); // Handle error as needed
    }
  };

  if (display === "AiStudio") {
    return (
      <TrackGenerationStudio
        homeCompany={homeCompany}
        homeStore={props.homeStore}
        initialTargetKind={studioInitialKind}
        // Existing LMS content for the "generate from existing
        // content" source picker — already fetched for the main
        // displays, so the studio gets it without an extra call.
        modulesData={data.modules?.redux?.payload?.learning_modules || []}
        sectionsData={data.sections?.redux?.payload?.learning_sections || []}
        // Already-fetched company role list — passed in so the studio's
        // full-LMS mode can render its roles picker without an extra
        // round-trip. `api/role_names` returns a raw JSON array (not the
        // redux envelope) so `data.roleNames` is the array directly.
        // Fallback to [] while still loading on first mount.
        roles={Array.isArray(data.roleNames) ? data.roleNames : []}
        onSaved={() => fetch_data(default_api_calls, api_params, false)}
        onClose={() => setDisplay("Tracks")}
      />
    );
  }

  return (
    <>
      {!isLoading ? (
        <div className="w-full min-h-screen flex">
          <div className="w-1/5">
            <div className="h-full flex flex-col gap-1 px-6 py-12 bg-slate-50 border-r border-slate-200">
              <Tooltip
                title="Generate a complete track — modules, sections, and quizzes — from a prompt and your reference docs."
                placement="right"
                arrow
              >
                <button
                  className={`w-full py-3 px-4 text-left flex items-center relative rounded-2xl mb-3 ${
                    display === "AiStudio"
                      ? "bg-gradient-to-r from-violet-700 to-violet-600 text-white shadow-md"
                      : "bg-white border border-violet-200 hover:border-violet-400 text-violet-700"
                  }`}
                  onClick={() => {
                    setStudioInitialKind(null);
                    setDisplay("AiStudio");
                  }}
                >
                  <div className="flex flex-1 items-center space-x-4">
                    <div>
                      <AutoAwesomeIcon />
                    </div>
                    <span className="font-medium">AI Studio</span>
                  </div>
                </button>
              </Tooltip>
              <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold px-4 pb-1">
                Course Structure
              </div>
              <Tooltip
                title="Top-level training programs. A track groups related modules into a single course an employee progresses through."
                placement="right"
                arrow
              >
                <button
                  className={`w-full py-3 px-4 text-left text-base flex items-center relative rounded-2xl ${
                    display === "Tracks" ? "bg-white" : ""
                  }`}
                  onClick={() => {
                    setModalDataSection(default_data_section);
                    setDisplay("Tracks");
                  }}
                >
                  <div
                    className={`flex flex-1 items-center space-x-4 ${
                      display === "Tracks" ? "text-violet-700" : "text-slate-500"
                    }`}
                  >
                    <div>
                      <SchoolIcon />
                    </div>
                    <span>Tracks</span>
                  </div>
                </button>
              </Tooltip>
              <Tooltip
                title="Chapter-level groupings inside a track. Each module bundles related sections so admins can organize learning into logical units."
                placement="right"
                arrow
              >
                <button
                  className={`w-full py-3 px-4 text-left flex items-center relative rounded-2xl ${
                    display === "Modules" ? "bg-white" : ""
                  }`}
                  onClick={() => {
                    setModalDataSection(default_data_section);
                    setDisplay("Modules");
                  }}
                >
                  <div
                    className={`flex flex-1 items-center space-x-4 ${
                      display === "Modules" ? "text-violet-700" : "text-slate-500"
                    }`}
                  >
                    <div>
                      <ViewModuleIcon />
                    </div>
                    <span>Modules</span>
                  </div>
                </button>
              </Tooltip>
              <Tooltip
                title="The pages where learning actually happens. A section holds the rich content — text, images, videos — and is where you embed quizzes and swipe decks."
                placement="right"
                arrow
              >
                <button
                  className={`w-full py-3 px-4 text-left flex items-center relative rounded-2xl ${
                    display === "Sections" ? "bg-white" : ""
                  }`}
                  onClick={() => setDisplay("Sections")}
                >
                  <div
                    className={`flex flex-1 items-center space-x-4 ${
                      display === "Sections"
                        ? "text-violet-700"
                        : "text-slate-500"
                    }`}
                  >
                    <div>
                      <ViewHeadlineIcon />
                    </div>
                    <span>Sections</span>
                  </div>
                </button>
              </Tooltip>

              {/* Embeddable content: standalone, embeddable assets that
                  drop into any section. Separated from the structure
                  group above because the authoring/reuse model differs —
                  these are libraries, not hierarchy. */}
              <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold px-4 pt-5 pb-1 mt-2 border-t border-slate-200 -mx-2">
                <span className="px-2">Embeddable Content</span>
              </div>
              <Tooltip
                title="Reusable question sets that can be embedded in any section. Authored once, dropped into multiple courses."
                placement="right"
                arrow
              >
                <button
                  className={`w-full py-3 px-4 text-left flex items-center relative rounded-2xl ${
                    display === "Quizzes" ? "bg-white" : ""
                  }`}
                  onClick={() => setDisplay("Quizzes")}
                >
                  <div
                    className={`flex flex-1 items-center space-x-4 ${
                      display === "Quizzes" ? "text-violet-700" : "text-slate-500"
                    }`}
                  >
                    <div>
                      <QuizOutlinedIcon />
                    </div>
                    <span>Quizzes</span>
                  </div>
                </button>
              </Tooltip>
              <Tooltip
                title="Reusable card decks for fast, dynamic learning. TikTok-style swipeable cards with images, videos, polls, and quizzes — embeddable in any section."
                placement="right"
                arrow
              >
                <button
                  className={`w-full py-3 px-4 text-left flex items-center relative rounded-2xl ${
                    display === "SwipeDecks" ? "bg-white" : ""
                  }`}
                  onClick={() => setDisplay("SwipeDecks")}
                >
                  <div
                    className={`flex flex-1 items-center space-x-4 ${
                      display === "SwipeDecks" ? "text-violet-700" : "text-slate-500"
                    }`}
                  >
                    <div>
                      <StyleOutlinedIcon />
                    </div>
                    <span>Swipe Decks</span>
                  </div>
                </button>
              </Tooltip>
              <Tooltip
                title="Reusable forms — rubrics, checklists, note logs, daily feedback, and self-reflection. Completed by the learner or a supervisor; embeddable in any section."
                placement="right"
                arrow
              >
                <button
                  className={`w-full py-3 px-4 text-left flex items-center relative rounded-2xl ${
                    display === "Forms" ? "bg-white" : ""
                  }`}
                  onClick={() => setDisplay("Forms")}
                >
                  <div
                    className={`flex flex-1 items-center space-x-4 ${
                      display === "Forms" ? "text-violet-700" : "text-slate-500"
                    }`}
                  >
                    <div>
                      <DescriptionOutlinedIcon />
                    </div>
                    <span>Forms</span>
                  </div>
                </button>
              </Tooltip>
              <Tooltip
                title="Practice scenarios: conversations employees rehearse with an AI character, such as an angry guest or allergen question, with coaching feedback. Embeddable in any section."
                placement="right"
                arrow
              >
                <button
                  className={`w-full py-3 px-4 text-left flex items-center relative rounded-2xl ${
                    display === "Roleplays" ? "bg-white" : ""
                  }`}
                  onClick={() => setDisplay("Roleplays")}
                >
                  <div
                    className={`flex flex-1 items-center space-x-4 ${
                      display === "Roleplays" ? "text-violet-700" : "text-slate-500"
                    }`}
                  >
                    <div>
                      <ConnectWithoutContactIcon />
                    </div>
                    <span>Practice Scenarios</span>
                  </div>
                </button>
              </Tooltip>

              {/* Recognition: credentials earned by completing tracks. Not
                  embeddable content — badges attach to a TRACK (its
                  completion awards them), so they get their own group. */}
              <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold px-4 pt-5 pb-1 mt-2 border-t border-slate-200 -mx-2">
                <span className="px-2">Recognition</span>
              </div>
              <Tooltip
                title="Badges your team earns by completing tracks, like Certified Trainer. Shown on the employee profile's Training Badges tab."
                placement="right"
                arrow
              >
                <button
                  className={`w-full py-3 px-4 text-left flex items-center relative rounded-2xl ${
                    display === "Badges" ? "bg-white" : ""
                  }`}
                  onClick={() => setDisplay("Badges")}
                >
                  <div
                    className={`flex flex-1 items-center space-x-4 ${
                      display === "Badges" ? "text-violet-700" : "text-slate-500"
                    }`}
                  >
                    <div>
                      <WorkspacePremiumIcon />
                    </div>
                    <span>Badges</span>
                  </div>
                </button>
              </Tooltip>
            </div>
          </div>
          <div className="w-4/5 min-h-screen">
            <div>
              <TopBarDisplayCreateTrainingContent
                title={"Create Training Content"}
                storeOptions={props.storeOptions}
                homeStore={props.homeStore}
                storeNames={props.storeNames}
                change_home_store={props.change_home_store}
              />
            </div>
            <div>
              {display === "Tracks" ? (
                <TracksDisplay
                  onGenerateWithAi={() => handleGenerateWithAi("track")}
                  tracksData={data.tracks?.redux?.payload?.learning_tracks}
                  modulesData={data.modules?.redux?.payload?.learning_modules}
                  sectionsData={
                    data.sections?.redux?.payload?.learning_sections
                  }
                  quizzesData={data.quizzes?.redux?.payload?.learning_quizzes}
                  roleNames={data.roleNames}
                  homeCompany={homeCompany}
                  homeStore={props.homeStore}
                  modalDataTrack={modalDataTrack}
                  setModalDataTrack={setModalDataTrack}
                  default_data_track={default_data_track}
                  modalDataModule={modalDataModule}
                  setModalDataModule={setModalDataModule}
                  default_data_module={default_data_module}
                  modalDataSection={modalDataSection}
                  setModalDataSection={setModalDataSection}
                  default_data_section={default_data_section}
                  post_learning_track={post_learning_track}
                  delete_learning_track={delete_learning_track}
                  post_learning_module={post_learning_module}
                  delete_learning_module={delete_learning_module}
                  post_learning_section={post_learning_section}
                  delete_learning_section={delete_learning_section}
                  modalDataQuiz={modalDataQuiz}
                  setModalDataQuiz={setModalDataQuiz}
                  default_data_quiz={default_data_quiz}
                  post_learning_quiz={post_learning_quiz}
                  post_learning_track_enrollment_by_roles={
                    post_learning_track_enrollment_by_roles
                  }
                />
              ) : display === "Modules" ? (
                <ModulesDisplay
                  onGenerateWithAi={() => handleGenerateWithAi("module")}
                  modulesData={data.modules?.redux?.payload?.learning_modules}
                  sectionsData={
                    data.sections?.redux?.payload?.learning_sections
                  }
                  quizzesData={data.quizzes?.redux?.payload?.learning_quizzes}
                  homeCompany={homeCompany}
                  homeStore={props.homeStore}
                  modalDataModule={modalDataModule}
                  setModalDataModule={setModalDataModule}
                  default_data_module={default_data_module}
                  modalDataSection={modalDataSection}
                  setModalDataSection={setModalDataSection}
                  default_data_section={default_data_section}
                  post_learning_module={post_learning_module}
                  delete_learning_module={delete_learning_module}
                  post_learning_section={post_learning_section}
                  delete_learning_section={delete_learning_section}
                  modalDataQuiz={modalDataQuiz}
                  setModalDataQuiz={setModalDataQuiz}
                  default_data_quiz={default_data_quiz}
                  post_learning_quiz={post_learning_quiz}
                />
              ) : display === "Sections" ? (
                <SectionsDisplay
                  sectionsData={
                    data.sections?.redux?.payload?.learning_sections
                  }
                  modulesData={data.modules?.redux?.payload?.learning_modules}
                  quizzesData={data.quizzes?.redux?.payload?.learning_quizzes}
                  homeCompany={homeCompany}
                  homeStore={props.homeStore}
                  modalDataSection={modalDataSection}
                  setModalDataSection={setModalDataSection}
                  default_data_section={default_data_section}
                  post_learning_section={post_learning_section}
                  delete_learning_section={delete_learning_section}
                  modalDataQuiz={modalDataQuiz}
                  setModalDataQuiz={setModalDataQuiz}
                  default_data_quiz={default_data_quiz}
                  post_learning_quiz={post_learning_quiz}
                />
              ) : display === "Quizzes" ? (
                <QuizzesDisplay
                  quizzesData={data.quizzes?.redux?.payload?.learning_quizzes}
                  homeCompany={homeCompany}
                  modalDataQuiz={modalDataQuiz}
                  setModalDataQuiz={setModalDataQuiz}
                  default_data_quiz={default_data_quiz}
                  post_learning_quiz={post_learning_quiz}
                  delete_learning_quiz={delete_learning_quiz}
                  sectionsData={
                    data.sections?.redux?.payload?.learning_sections
                  }
                  modulesData={data.modules?.redux?.payload?.learning_modules}
                />
              ) : display === "SwipeDecks" ? (
                <>
                  <SwipeDecksDisplay
                    key={swipeDeckRefreshKey}
                    homeCompany={homeCompany}
                    onCreate={() => {
                      setModalDataDeck(null);
                      setSwipeDeckModalOpen(true);
                    }}
                    onEdit={(deck) => {
                      setModalDataDeck({ uuid: deck.uuid });
                      setSwipeDeckModalOpen(true);
                    }}
                    onPreview={(deck) => {
                      // Dashboard preview — fetch via admin endpoint
                      setSwipeDeckPreviewState({ deckUuid: deck.uuid });
                    }}
                  />
                  <SwipeDeckInputsModal
                    open={swipeDeckModalOpen}
                    onClose={() => setSwipeDeckModalOpen(false)}
                    modalDataDeck={modalDataDeck}
                    homeCompany={homeCompany}
                    sectionsData={
                      data.sections?.redux?.payload?.learning_sections
                    }
                    modulesData={
                      data.modules?.redux?.payload?.learning_modules
                    }
                    onSaved={() => {
                      // Force the dashboard to remount + re-fetch
                      setSwipeDeckRefreshKey((k) => k + 1);
                    }}
                    onPublicShareChange={() => {
                      // A link toggled without saving the deck still changes
                      // what the list shows.
                      setSwipeDeckRefreshKey((k) => k + 1);
                    }}
                    onPreview={(deckInEditor) => {
                      // Editor preview — pass the in-progress (possibly
                      // unsaved) deck object directly so the player skips
                      // a fetch and renders local state with answer keys.
                      setSwipeDeckPreviewState({
                        previewDeck: denormalizeEditorDeckForPreview(deckInEditor),
                      });
                    }}
                  />
                  {swipeDeckPreviewState && (
                    <React.Suspense
                      fallback={
                        <div className="fixed inset-0 z-[1400] bg-black/95 flex items-center justify-center">
                          <div className="text-white/80 text-sm">Loading…</div>
                        </div>
                      }
                    >
                      <SwipeDeckPlayerLazy
                        deckUuid={swipeDeckPreviewState.deckUuid}
                        previewDeck={swipeDeckPreviewState.previewDeck}
                        previewMode
                        companyId={homeCompany}
                        onClose={() => setSwipeDeckPreviewState(null)}
                      />
                    </React.Suspense>
                  )}
                </>
              ) : display === "Forms" ? (
                <>
                  <FormsDisplay
                    key={formsRefreshKey}
                    homeCompany={homeCompany}
                    onCreate={() => {
                      setModalDataForm({});
                      setFormModalOpen(true);
                    }}
                    onEdit={(form) => {
                      setModalDataForm({ uuid: form.uuid });
                      setFormModalOpen(true);
                    }}
                  />
                  <FormEditorModal
                    open={formModalOpen}
                    handleClose={() => setFormModalOpen(false)}
                    modalDataForm={modalDataForm || {}}
                    homeCompany={homeCompany}
                    sectionsData={data.sections?.redux?.payload?.learning_sections}
                    modulesData={data.modules?.redux?.payload?.learning_modules}
                    onSaveSuccess={() => setFormsRefreshKey((k) => k + 1)}
                    onPublicShareChange={() => setFormsRefreshKey((k) => k + 1)}
                  />
                </>
              ) : display === "Roleplays" ? (
                <>
                  <RoleplaysDisplay
                    key={roleplaysRefreshKey}
                    homeCompany={homeCompany}
                    onCreate={() => {
                      setModalDataRoleplay({});
                      setRoleplayModalOpen(true);
                    }}
                    onEdit={(roleplay) => {
                      setModalDataRoleplay({ uuid: roleplay.uuid });
                      setRoleplayModalOpen(true);
                    }}
                  />
                  <RoleplayEditorModal
                    open={roleplayModalOpen}
                    handleClose={() => setRoleplayModalOpen(false)}
                    modalDataRoleplay={modalDataRoleplay || {}}
                    homeCompany={homeCompany}
                    sectionsData={data.sections?.redux?.payload?.learning_sections}
                    modulesData={data.modules?.redux?.payload?.learning_modules}
                    onSaveSuccess={() => setRoleplaysRefreshKey((k) => k + 1)}
                  />
                </>
              ) : display === "Badges" ? (
                <>
                  <BadgesDisplay
                    key={badgesRefreshKey}
                    homeCompany={homeCompany}
                    onCreate={() => {
                      setModalDataBadge({});
                      setBadgeModalOpen(true);
                    }}
                    onEdit={(badge) => {
                      setModalDataBadge(badge);
                      setBadgeModalOpen(true);
                    }}
                  />
                  <BadgeEditorModal
                    open={badgeModalOpen}
                    handleClose={() => setBadgeModalOpen(false)}
                    modalDataBadge={modalDataBadge || {}}
                    homeCompany={homeCompany}
                    onSaveSuccess={() => setBadgesRefreshKey((k) => k + 1)}
                  />
                </>
              ) : (
                <div></div>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
          }}
        >
          <LoadSpinner />
        </div>
      )}
    </>
  );
}

export default CreateTrainingContentDisplay;
